import { useMemo } from "react";
import { motion } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { SunRoofGlyph } from "@/components/green/SunRoofGlyph";
import { HUDStatusBadge } from "@/components/HUDStatusBadge";
import { deriveGreenMetrics } from "@/store/greenStore";
import { useMaterialsStore } from "@/store/materialsStore";

const EASE = [0.22, 1, 0.36, 1] as const;
const fmt = (n: number) => n.toLocaleString("en-US").replace(/,/g, " ");

const card = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.09, duration: 0.6, ease: EASE } }),
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

const Stat = ({ label, value, testId }: { label: string; value: string; testId: string }) => (
  <div className="flex flex-col gap-1">
    <Eyebrow>{label}</Eyebrow>
    <span data-testid={testId} className="font-mono text-sm text-white">{value}</span>
  </div>
);

export default function GreenIntelligenceScreen() {
  const items = useMaterialsStore((s) => s.items);
  const g = useMemo(() => deriveGreenMetrics(items), [items]);
  const swap = g.ecoSwaps[0];

  return (
    <AppShell>
      <section data-testid="green-screen" className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Eyebrow>Sustainability · Level 2</Eyebrow>
            <h1 data-testid="green-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Green Intelligence</h1>
          </div>
          <span data-testid="green-compliance-badge">
            <HUDStatusBadge status={`COMPLIANCE ${g.compliancePct}%`} tone="energy" />
          </span>
        </motion.div>

        <motion.div initial="hidden" animate="show" className="grid grid-cols-1 gap-4 md:grid-cols-6 md:gap-5">
          <motion.div variants={card} custom={0} className="md:col-span-2">
            <BentoCard data-testid="card-embodied-carbon" className="flex h-full flex-col justify-between gap-8 p-6 md:p-7">
              <div className="flex items-start justify-between">
                <Eyebrow>Embodied Carbon</Eyebrow>
                <span aria-hidden className="h-2 w-2 rounded-full bg-energy shadow-[0_0_14px_2px_rgba(16,185,129,0.6)]" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline gap-2">
                  <span data-testid="embodied-carbon-value" className="font-display text-4xl font-bold leading-none tracking-tight text-energy sm:text-5xl">{fmt(g.embodiedCarbonKg)}</span>
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-energy/80">kg CO₂e</span>
                </div>
                <span data-testid="carbon-per-m2" className="font-mono text-[10px] uppercase tracking-[0.18em] text-cad">{fmt(g.carbonPerM2)} kg CO₂e / m² · derived from takeoff</span>
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={1} className="md:col-span-4">
            <BentoCard data-testid="card-solar-passive" className="flex h-full flex-col gap-6 p-6 md:flex-row md:items-center md:gap-8 md:p-7">
              <div className="flex flex-1 flex-col gap-6">
                <div className="flex flex-col gap-1.5">
                  <Eyebrow>Solar &amp; Passive Energy</Eyebrow>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-white">Roof-mounted PV potential</h2>
                </div>
                <div className="flex items-baseline gap-2">
                  <span data-testid="solar-yield-value" className="font-display text-3xl font-bold leading-none tracking-tight text-energy sm:text-4xl">{fmt(g.solar.annualYieldKwh)}</span>
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-energy/80">kWh / yr est.</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Stat label="Pitch" value={`${g.solar.roofPitchDeg}°`} testId="solar-pitch" />
                  <Stat label="Facing" value={g.solar.orientation} testId="solar-orientation" />
                  <Stat label="Passive gain" value={`+${g.solar.passiveGainPct}%`} testId="solar-passive" />
                </div>
              </div>
              <div className="h-40 w-full md:h-44 md:w-[300px]">
                <SunRoofGlyph pitch={g.solar.roofPitchDeg} />
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={2} className="md:col-span-6">
            <BentoCard data-testid="card-eco-swaps" className="flex flex-col gap-5 p-6 md:p-7">
              <div className="flex flex-col gap-1.5">
                <Eyebrow>Eco-Swap Recommendations</Eyebrow>
                <h2 className="font-display text-lg font-semibold tracking-tight text-white">Highest-impact substitution</h2>
              </div>
              <div data-testid="eco-swap-item" className="flex flex-col gap-4 rounded-2xl border border-energy/30 bg-energy/[0.04] p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-3 font-sans text-sm">
                    <span className="text-white/55 line-through decoration-cad">{swap.from}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" className="text-energy" aria-hidden><path d="M2 8h11M9 4l4 4-4 4" /></svg>
                    <span data-testid="eco-swap-to" className="font-medium text-white">{swap.to}</span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cad">{swap.note}</span>
                </div>
                <div className="flex shrink-0 items-baseline gap-1.5 rounded-xl border border-energy/50 px-4 py-2.5">
                  <span data-testid="eco-swap-reduction" className="font-display text-2xl font-bold leading-none text-energy">−{swap.reductionPct}%</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-energy/80">CO₂e</span>
                </div>
              </div>
            </BentoCard>
          </motion.div>
        </motion.div>
      </section>
    </AppShell>
  );
}
